// client/src/components/common/PhoneActionBar.tsx
//
// The sticky bottom action bar of a phone page (ST-00207, m4b-contracts §5): the page's main
// actions — approve / reject / escalate on a deviation, dispatch / re-plan / replay / cancel /
// abort on a trip — stay under the thumb while the page scrolls. Sticky inside the content pane
// (not fixed), so it follows the pane's width beside a sidebar on a tablet; it bleeds across the
// pane padding and sits flush on the bottom edge at the end of the page (PhoneLayout.css).
// Buttons are at least 48 px tall.
//
// Put it last in the page's root element; the phone layouts make that root a min-h-screen
// flex column so the bar rests on the screen's bottom edge even when the page is short.

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { IconType } from 'react-icons';
import './PhoneLayout.css';

export type PhoneActionVariant = 'primary' | 'secondary' | 'danger';

/** Classes for a bar button, or a router Link styled as one. */
export const phoneActionClass = (variant: PhoneActionVariant = 'secondary'): string =>
  `phone-action${variant === 'secondary' ? '' : ` phone-action--${variant}`}`;

export interface PhoneActionBarProps {
  /** Accessible name of the group of actions ("Decide on this deviation", "Trip actions"). */
  label: string;
  children: ReactNode;
}

export default function PhoneActionBar({ label, children }: PhoneActionBarProps) {
  return (
    <div className="phone-action-bar" role="group" aria-label={label} data-phone-action-bar="">
      {children}
    </div>
  );
}

export interface PhoneActionProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: PhoneActionVariant;
  icon?: IconType;
  children: ReactNode;
}

/** A button in the bar: icon + short label, 48 px tall, the bar's width shared equally. */
export function PhoneAction({ variant = 'secondary', icon: Icon, children, type = 'button', className = '', ...rest }: PhoneActionProps) {
  return (
    <button type={type} className={`${phoneActionClass(variant)}${className ? ` ${className}` : ''}`} {...rest}>
      {Icon && <Icon size={14} aria-hidden="true" />}
      <span>{children}</span>
    </button>
  );
}
